/**
 * MentionAutocomplete — Dropdown component triggered by `@` in the editor.
 *
 * Fetches users (library members for private libraries, all users for public)
 * and displays matching names. On selection, inserts a styled mention link.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import * as pagesApi from '../../api/pages-api';

interface MentionAutocompleteProps {
  readonly editor: BlockNoteEditor<any, any, any>;
  readonly libraryId: string;
  readonly position: { top: number; left: number } | null;
  readonly searchText: string;
  readonly onSelect: (userId: string, displayName: string) => void;
  readonly onClose: () => void;
}

interface UserResult {
  readonly userId: string;
  readonly displayName: string;
}

export function MentionAutocomplete({
  editor: _editor,
  libraryId,
  position,
  searchText,
  onSelect,
  onClose,
}: MentionAutocompleteProps): React.ReactElement | null {
  const [results, setResults] = useState<UserResult[]>([]);
  const [allUsers, setAllUsers] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);

  // Fetch users on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    async function fetchUsers(): Promise<void> {
      setLoading(true);
      try {
        // Try library members first, fall back to general user list
        const response = await pagesApi.listMembers(libraryId);
        if (response.data.length > 0) {
          // Members don't have displayName directly — fetch user list for names
          const usersResponse = await pagesApi.listUsers();
          setAllUsers(
            usersResponse.data.map((u) => ({
              userId: u.userId,
              displayName: u.displayName,
            }))
          );
        } else {
          const usersResponse = await pagesApi.listUsers();
          setAllUsers(
            usersResponse.data.map((u) => ({
              userId: u.userId,
              displayName: u.displayName,
            }))
          );
        }
      } catch {
        // If members endpoint fails, try general users
        try {
          const usersResponse = await pagesApi.listUsers();
          setAllUsers(
            usersResponse.data.map((u) => ({
              userId: u.userId,
              displayName: u.displayName,
            }))
          );
        } catch {
          setAllUsers([]);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, [libraryId]);

  // Filter results by search text
  useEffect(() => {
    if (!searchText) {
      setResults(allUsers.slice(0, 10));
      setSelectedIndex(0);
      return;
    }
    const query = searchText.toLowerCase();
    const filtered = allUsers.filter((u) =>
      u.displayName.toLowerCase().includes(query)
    );
    setResults(filtered.slice(0, 10));
    setSelectedIndex(0);
  }, [searchText, allUsers]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!position) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelect(results[selectedIndex].userId, results[selectedIndex].displayName);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [position, results, selectedIndex, onSelect, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  if (!position) return null;

  return (
    <div
      ref={containerRef}
      className="absolute z-50 w-64 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg"
      style={{ top: position.top, left: position.left }}
      role="listbox"
      aria-label="Mention suggestions"
    >
      {loading && (
        <div className="px-3 py-2 text-sm text-muted-foreground">Loading users...</div>
      )}

      {!loading && results.length === 0 && (
        <div className="px-3 py-2 text-sm text-muted-foreground">No users found</div>
      )}

      {!loading &&
        results.map((user, index) => (
          <button
            key={user.userId}
            type="button"
            role="option"
            aria-selected={index === selectedIndex}
            className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors
              ${index === selectedIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
            `}
            onClick={() => onSelect(user.userId, user.displayName)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span className="truncate block">@{user.displayName}</span>
          </button>
        ))}
    </div>
  );
}

/**
 * Hook to detect `@` trigger in the editor and manage mention autocomplete state.
 */
export function useMentionTrigger(editor: BlockNoteEditor<any, any, any> | null) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!editor) return;

    const handleChange = () => {
      try {
        const cursor = editor.getTextCursorPosition();
        if (!cursor || !cursor.block) return;

        const block = cursor.block;
        const textContent = extractTextFromBlock(block);

        // Find the last @ that isn't preceded by a word character (start of mention)
        const atIndex = findMentionTrigger(textContent);
        if (atIndex === -1) {
          if (isOpen) {
            setIsOpen(false);
            setSearchText('');
            setPosition(null);
          }
          return;
        }

        // Extract search text after @
        const afterAt = textContent.slice(atIndex + 1);

        // If there's a space after the search text, close
        // Allow word characters only (letters, numbers, spaces within name)
        if (afterAt.includes('\n')) {
          if (isOpen) {
            setIsOpen(false);
            setSearchText('');
            setPosition(null);
          }
          return;
        }

        setSearchText(afterAt);

        if (!isOpen) {
          // Position the dropdown near the cursor
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            setPosition({
              top: rect.bottom + 4,
              left: rect.left,
            });
          }
          setIsOpen(true);
        }
      } catch {
        // Ignore errors in text extraction
      }
    };

    editor.onChange(handleChange);

    return () => {
      // BlockNote onChange cleanup handled by component unmount
    };
  }, [editor, isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchText('');
    setPosition(null);
  }, []);

  return { isOpen, searchText, position, close };
}

/**
 * Find the position of an `@` mention trigger in text.
 * The `@` must be at the start of the text or preceded by a space/punctuation.
 */
function findMentionTrigger(text: string): number {
  // Search from the end for the last unresolved @ trigger
  for (let i = text.length - 1; i >= 0; i--) {
    if (text[i] === '@') {
      // Valid trigger: at start or preceded by whitespace/punctuation
      if (i === 0 || /[\s\n([\-{]/.test(text[i - 1])) {
        return i;
      }
    }
  }
  return -1;
}

/**
 * Extract plain text from a BlockNote block's content array.
 */
function extractTextFromBlock(block: any): string {
  if (!block.content || !Array.isArray(block.content)) return '';
  return block.content
    .map((item: any) => {
      if (item.type === 'text') return item.text || '';
      if (item.type === 'link') {
        return (item.content || []).map((c: any) => c.text || '').join('');
      }
      return '';
    })
    .join('');
}
